import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const EMPTY = { name: "", email: "", phone: "", wedding_date: "", venue: "", music_style: "" };

const FIELD_STYLE =
    "bg-transparent border-white/20 text-[#fafafa] placeholder:text-[#71717a] rounded-none h-12 focus-visible:ring-1 focus-visible:ring-[#d4af37] focus-visible:border-[#d4af37]";

export const EnquiryForm = () => {
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const body = new URLSearchParams({ "form-name": "enquiry", "bot-field": "", ...form });
            const res = await fetch("/", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: body.toString(),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            toast.success("Thank you — your enquiry has been received. We look forward to hearing from you.");
            setForm(EMPTY);
        } catch (err) {
            toast.error("Something went wrong sending your enquiry. Please call 310.877.0250 instead.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="enquire" data-testid="enquiry-section" className="py-24 md:py-32">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-14">
                <Reveal className="lg:col-span-2">
                    <p className="text-xs uppercase tracking-[0.35em] text-[#d4af37] mb-4">Book The Orchestra</p>
                    <h2 className="font-serif text-4xl sm:text-5xl tracking-tight font-light text-[#fafafa]">
                        Contact <em className="text-[#d4af37]">us.</em>
                    </h2>
                    <p className="mt-6 text-base sm:text-lg text-[#a1a1aa] leading-relaxed">
                        Tell us about your wedding — the date, the venue, the music you love — and we&rsquo;ll discuss
                        the endless possibilities and our custom packages.
                    </p>
                    <div className="mt-10 space-y-5">
                        <a href="tel:+13108770250" data-testid="enquiry-call-link"
                            className="flex items-center gap-4 text-[#fafafa] hover:text-[#d4af37] transition-colors duration-300">
                            <Phone size={18} className="text-[#d4af37]" /> <span className="tracking-wide">310.877.0250</span>
                        </a>
                        <a href="mailto:Info@agnmusic.com" data-testid="enquiry-email-link"
                            className="flex items-center gap-4 text-[#fafafa] hover:text-[#d4af37] transition-colors duration-300">
                            <Mail size={18} className="text-[#d4af37]" /> <span className="tracking-wide">Info@agnmusic.com</span>
                        </a>
                        <p className="flex items-center gap-4 text-[#a1a1aa]" data-testid="enquiry-location">
                            <MapPin size={18} className="text-[#d4af37]" /> <span className="tracking-wide">Beverly Hills, California</span>
                        </p>
                    </div>
                </Reveal>
                <Reveal delay={0.15} className="lg:col-span-3">
                    <form onSubmit={submit} data-testid="enquiry-form"
                        className="border border-white/10 bg-[#121216] p-8 md:p-10 space-y-6">
                        <div className="grid sm:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <Label htmlFor="enquiry-name" className="text-xs uppercase tracking-[0.2em] text-[#a1a1aa]">Name *</Label>
                                <Input id="enquiry-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")}
                                    placeholder="Your name" className={FIELD_STYLE} />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="enquiry-email" className="text-xs uppercase tracking-[0.2em] text-[#a1a1aa]">Email *</Label>
                                <Input id="enquiry-email" data-testid="enquiry-email-input" type="email" required value={form.email} onChange={set("email")}
                                    placeholder="you@example.com" className={FIELD_STYLE} />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="enquiry-phone" className="text-xs uppercase tracking-[0.2em] text-[#a1a1aa]">Phone</Label>
                                <Input id="enquiry-phone" data-testid="enquiry-phone-input" type="tel" value={form.phone} onChange={set("phone")}
                                    placeholder="(310) 555-0123" className={FIELD_STYLE} />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="enquiry-date" className="text-xs uppercase tracking-[0.2em] text-[#a1a1aa]">Wedding Date</Label>
                                <Input id="enquiry-date" data-testid="enquiry-date-input" type="date" value={form.wedding_date} onChange={set("wedding_date")}
                                    className={FIELD_STYLE} />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="enquiry-venue" className="text-xs uppercase tracking-[0.2em] text-[#a1a1aa]">Venue / Location</Label>
                            <Input id="enquiry-venue" data-testid="enquiry-venue-input" value={form.venue} onChange={set("venue")}
                                placeholder="e.g. The Beverly Hills Hotel — or a destination abroad" className={FIELD_STYLE} />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="enquiry-music" className="text-xs uppercase tracking-[0.2em] text-[#a1a1aa]">Music Styles You Love</Label>
                            <Textarea id="enquiry-music" data-testid="enquiry-music-input" rows={4} value={form.music_style} onChange={set("music_style")}
                                placeholder="Persian + Top 40, a string quartet for the ceremony, a full orchestra for the reception…"
                                className="bg-transparent border-white/20 text-[#fafafa] placeholder:text-[#71717a] rounded-none focus-visible:ring-1 focus-visible:ring-[#d4af37] focus-visible:border-[#d4af37]" />
                        </div>
                        <button type="submit" disabled={sending} data-testid="enquiry-submit-btn"
                            className="w-full inline-flex items-center justify-center px-8 py-4 text-xs uppercase tracking-[0.3em] bg-[#d4af37] text-[#08080a] hover:bg-[#c6a87c] disabled:opacity-50 transition-colors duration-300">
                            {sending ? "Sending…" : "Send Enquiry"}
                        </button>
                    </form>
                </Reveal>
            </div>
        </section>
    );
};
